import "@/emergent.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { SessionProvider } from "@/context/SessionContext";
import { CartProvider } from "@/context/CartContext";
import { OrderProvider } from "@/context/OrderContext";
import { LoyaltyProvider } from "@/context/LoyaltyContext";
import { UIProvider } from "@/context/UIContext";
import { isFullMenuMode } from "@/config/restaurantConfig";
import { GuestMenuGate } from "@/components/GuestMenuGate";

import { AppShell } from "@/components/AppShell";
import { ProductDetailSheet } from "@/components/ProductDetailSheet";
import { CartSheet } from "@/components/CartSheet";
import { CallWaiterSheet } from "@/components/CallWaiterSheet";
import { ServicesDrawer } from "@/components/ServicesDrawer";
import { ServiceFeedbackSheet } from "@/components/ServiceFeedbackSheet";
import { StaffRatingSheet } from "@/components/StaffRatingSheet";
import { Toaster } from "@/components/ui/sonner";

import EntryPage from "@/pages/EntryPage";
import HomePage from "@/pages/HomePage";
import MenuPage from "@/pages/MenuPage";
import OrderPage from "@/pages/OrderPage";
import ConfirmationPage from "@/pages/ConfirmationPage";

export function AppProviders({ children }) {
  return (
    <ThemeProvider>
      <LanguageProvider>
        {/* Real menu (API mode): tenant applied before any scoped state mounts. */}
        <GuestMenuGate>
          <SessionProvider>
            <CartProvider>
              <OrderProvider>
                <LoyaltyProvider>
                  <UIProvider>{children}</UIProvider>
                </LoyaltyProvider>
              </OrderProvider>
            </CartProvider>
          </SessionProvider>
        </GuestMenuGate>
      </LanguageProvider>
    </ThemeProvider>
  );
}

// COMPANION_ONLY boundary: menu, cart and ordering routes exist only in
// FULL_MENU mode. Direct navigation is redirected to the companion home.
export function FullMenuOnly({ children }) {
  return isFullMenuMode() ? children : <Navigate to="/home" replace />;
}

export function AppRoutes() {
  const fullMenu = isFullMenuMode();
  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/c/demo-table-12" replace />} />
        <Route path="/c/:tableSlug" element={<EntryPage />} />
        <Route element={<AppShell />}>
          <Route path="/home" element={<HomePage />} />
          <Route path="/menu" element={<FullMenuOnly><MenuPage /></FullMenuOnly>} />
          <Route path="/order" element={<FullMenuOnly><OrderPage /></FullMenuOnly>} />
        </Route>
        <Route path="/confirmation/:orderId" element={<FullMenuOnly><ConfirmationPage /></FullMenuOnly>} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>

      {/* Global overlays */}
      {fullMenu && <ProductDetailSheet />}
      {fullMenu && <CartSheet />}
      <CallWaiterSheet />
      <ServicesDrawer />
      <ServiceFeedbackSheet />
      <StaffRatingSheet />
      <Toaster position="top-center" richColors closeButton={false} />
    </>
  );
}

function App() {
  return (
    <AppProviders>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AppProviders>
  );
}

export default App;
